import { useEffect, useMemo, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import {
  Badge, Card, Select, SelectItem, Table, TableBody, TableCell, TableHead, TableHeaderCell, TableRow, Text, TextInput, Title,
} from '@tremor/react';
import DateRangePresets from '../components/DateRangePresets';
import EmptyState from '../components/EmptyState';
import ErrorRetry from '../components/ErrorRetry';
import TableSkeleton from '../components/TableSkeleton';
import DrillCell from '../components/DrillCell';
import { useDrilldown } from '../components/DrilldownProvider';
import ExportButtons from '../components/ExportButtons';
import type { ExportColumn } from '../lib/exportTable';
import { fetchSalesReturns } from '../api/returns';
import { useStoreFilter } from '../lib/storeFilter';
import { usePagination } from '../lib/usePagination';
import Pagination from '../components/Pagination';
import { localYMD } from '../lib/format';
import { t } from '../lib/i18n';
import type { ReturnReason, SalesReturnRow, User } from '../api/types';

const fmtMxn = new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN', minimumFractionDigits: 2 });
const fmtQty = new Intl.NumberFormat('es-MX', { maximumFractionDigits: 2 });

function dateNDaysAgo(n: number): string {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return localYMD(d);
}

function reasonBadge(r: ReturnReason) {
  const color = r === 'defect' ? 'red' : r === 'warranty' ? 'amber' : r === 'core' ? 'blue' : 'gray';
  return <Badge color={color}>{t.returns.reasons[r]}</Badge>;
}

export default function SalesReturns() {
  const user = useOutletContext<User>();
  const [from, setFrom] = useState(dateNDaysAgo(30));
  const [to, setTo] = useState(dateNDaysAgo(0));
  const [reason, setReason] = useState<ReturnReason | 'all'>('all');
  const [slid, setSlid] = useState('');
  // Shared header store (lib/storeFilter); SalesLayout renders the picker.
  const [sid] = useStoreFilter(user);
  const [rows, setRows] = useState<SalesReturnRow[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  // Ticket + account detail come from the app-wide drill-down stack (JCD-59).
  const { open: openDrill } = useDrilldown();
  const pg = usePagination(rows);

  const load = () => {
    setErr(null);
    setRows(null);
    fetchSalesReturns({ from, to, sid, slid: slid || undefined, reason })
      .then(setRows)
      .catch((e: Error) => setErr(e.message ?? String(e)));
  };
  useEffect(() => { load(); }, [from, to, reason, slid, sid]);

  const showCost = user.role !== 'staff';

  // Export columns mirror the table (cost column only when the role sees it).
  const exportColumns = useMemo<ExportColumn<SalesReturnRow>[]>(() => [
    { label: t.returns.cols.date,    value: (r) => r.idate },
    { label: t.returns.cols.ticket,  value: (r) => r.tnum, numeric: true },
    { label: t.returns.cols.account, value: (r) => (r.anum > 0 ? r.anum : ''), numeric: true },
    { label: t.returns.cols.store,   value: (r) => r.sid, numeric: true },
    { label: t.returns.cols.slid,    value: (r) => r.slid },
    { label: t.returns.cols.part,    value: (r) => `${r.line} ${r.part}` },
    { label: t.returns.cols.pdesc,   value: (r) => r.pdesc },
    { label: t.returns.cols.qty,     value: (r) => r.qty, numeric: true, pdf: (r) => fmtQty.format(r.qty) },
    { label: t.returns.cols.sell,    value: (r) => r.sell_mxn, numeric: true, pdf: (r) => fmtMxn.format(r.sell_mxn) },
    ...(showCost
      ? [{ label: t.returns.cols.pcost, value: (r: SalesReturnRow) => r.pcost_mxn ?? '', numeric: true, pdf: (r: SalesReturnRow) => (r.pcost_mxn == null ? '—' : fmtMxn.format(r.pcost_mxn)) }]
      : []),
    { label: t.returns.cols.reason,  value: (r) => t.returns.reasons[r.reason] },
  ], [showCost]);
  const totalSell = rows ? rows.reduce((acc, r) => acc + r.sell_mxn, 0) : 0;

  return (
    <div className="space-y-4">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <Title>{t.returns.salesTitle}</Title>
          <Text className="text-tremor-content text-sm">{t.returns.salesSubtitle}</Text>
        </div>
        <ExportButtons
          title={t.returns.salesTitle}
          filename={`Devoluciones ${from} ${to}`}
          meta={[
            `${from} – ${to}`,
            ...(sid !== null ? [`${t.filter.store} ${sid}`] : []),
            ...(reason !== 'all' ? [`${t.returns.cols.reason}: ${t.returns.reasons[reason]}`] : []),
            ...(slid ? [`${t.returns.cols.slid}: ${slid}`] : []),
          ]}
          printedBy={user.username}
          columns={exportColumns}
          rows={rows}
          orientation="landscape"
        />
      </div>

      <Card className="space-y-3">
        <DateRangePresets from={from} to={to} onChange={({ from: f, to: t2 }) => { setFrom(f); setTo(t2); }} />
        <div className="flex flex-wrap gap-3 items-end">
          <label className="text-sm">
            <div className="text-tremor-content text-xs mb-1">{t.returns.cols.reason}</div>
            <Select value={reason} onValueChange={(v) => setReason(v as typeof reason)} className="w-40">
              <SelectItem value="all">{t.returns.reasons.all}</SelectItem>
              <SelectItem value="core">{t.returns.reasons.core}</SelectItem>
              <SelectItem value="defect">{t.returns.reasons.defect}</SelectItem>
              <SelectItem value="warranty">{t.returns.reasons.warranty}</SelectItem>
              <SelectItem value="other">{t.returns.reasons.other}</SelectItem>
            </Select>
          </label>
          <label className="text-sm">
            <div className="text-tremor-content text-xs mb-1">{t.returns.cols.slid}</div>
            <TextInput value={slid} onValueChange={setSlid} placeholder="slid" className="w-32" />
          </label>
          <div className="ml-auto text-sm text-tremor-content">
            {rows?.length ?? 0} líneas · Importe {fmtMxn.format(totalSell)}
          </div>
        </div>
      </Card>

      {err && <ErrorRetry message={err} onRetry={load} />}
      {!err && rows === null && <TableSkeleton rows={6} cols={showCost ? 10 : 9} />}
      {!err && rows !== null && rows.length === 0 && <EmptyState message={t.returns.empty} />}
      {!err && rows !== null && rows.length > 0 && (
        <Card className="p-0 overflow-hidden">
          <Table>
            <TableHead>
              <TableRow>
                <TableHeaderCell>{t.returns.cols.date}</TableHeaderCell>
                <TableHeaderCell>{t.returns.cols.ticket}</TableHeaderCell>
                <TableHeaderCell>{t.returns.cols.account}</TableHeaderCell>
                <TableHeaderCell>{t.returns.cols.store}</TableHeaderCell>
                <TableHeaderCell>{t.returns.cols.slid}</TableHeaderCell>
                <TableHeaderCell>{t.returns.cols.part}</TableHeaderCell>
                <TableHeaderCell className="text-right">{t.returns.cols.qty}</TableHeaderCell>
                <TableHeaderCell className="text-right">{t.returns.cols.sell}</TableHeaderCell>
                {showCost && <TableHeaderCell className="text-right">{t.returns.cols.pcost}</TableHeaderCell>}
                <TableHeaderCell>{t.returns.cols.reason}</TableHeaderCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {pg.pageRows.map((r, i) => (
                <TableRow
                  key={`${r.sid}-${r.tnum}-${r.line}-${r.part}-${i}`}
                  className="cursor-pointer hover:bg-tremor-background-muted"
                  onClick={() => openDrill({ kind: 'tkt', sid: r.sid, tnum: r.tnum })}
                >
                  <TableCell>{r.idate}</TableCell>
                  <TableCell>{r.tnum}</TableCell>
                  <TableCell>
                    {r.anum > 0 ? (
                      <span onClick={(e) => e.stopPropagation()}>
                        <DrillCell target={{ kind: 'account', anum: r.anum }}>{r.anum}</DrillCell>
                      </span>
                    ) : '—'}
                  </TableCell>
                  <TableCell>{r.sid}</TableCell>
                  <TableCell>{r.slid || '—'}</TableCell>
                  <TableCell>{r.line} {r.part}<div className="text-xs text-tremor-content">{r.pdesc}</div></TableCell>
                  <TableCell className="text-right">{fmtQty.format(r.qty)}</TableCell>
                  <TableCell className="text-right">{fmtMxn.format(r.sell_mxn)}</TableCell>
                  {showCost && (
                    <TableCell className="text-right">
                      {r.pcost_mxn == null ? '—' : fmtMxn.format(r.pcost_mxn)}
                    </TableCell>
                  )}
                  <TableCell>{reasonBadge(r.reason)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <Pagination {...pg} className="px-4" />
        </Card>
      )}

    </div>
  );
}
